(() => {
  if (customElements.get('campaign-price')) return;

  // Shared across Top Deals and SKU cards. Regios calculates quantity-one
  // prices in the current customer/cart context; no hypothetical bulk tiers.
  const cache = new Map();
  const queue = [];
  let running = 0;
  let revision = 0;
  const events = ['on:cart:add', 'on:line-item:change', 'on:cart:change'];

  function drain() {
    while (running < 6 && queue.length) {
      const job = queue.shift();
      running += 1;
      Promise.resolve().then(job.run).then(job.resolve, job.reject).finally(() => {
        running -= 1;
        drain();
      });
    }
  }

  function nativePrice(variant) {
    return {
      sale: variant.priceInCents,
      regular: Math.max(variant.priceInCents, variant.compareAtPriceInCents || 0)
    };
  }

  function summarize(prices) {
    let cheapest = null;
    let percent = 0;
    let minimumPercent = Infinity;
    for (const price of prices) {
      if (!Number.isFinite(price.sale) || !Number.isFinite(price.regular) || price.sale < 0) {
        throw new Error('Invalid campaign price');
      }
      if (!cheapest || price.sale < cheapest.sale) cheapest = price;
      const reduction = price.regular > 0 ? Math.max(0, Math.round((1 - price.sale / price.regular) * 100)) : 0;
      percent = Math.max(percent, reduction);
      minimumPercent = Math.min(minimumPercent, reduction);
    }
    return cheapest ? { ...cheapest, percent, uniformDiscount: minimumPercent > 0 && minimumPercent === percent } : null;
  }

  function calculate(product, variant) {
    const currency = window.Shopify?.currency?.active || '';
    const key = JSON.stringify([revision, currency, product.productId, variant.id, variant.priceInCents, variant.compareAtPriceInCents]);
    if (cache.has(key)) return cache.get(key);
    const requestedRevision = revision;
    const pending = new Promise((resolve, reject) => {
      queue.push({ resolve, reject, run: async () => {
        if (requestedRevision !== revision) throw new Error('Campaign context changed');
        const api = window.RegiosDOPP?.api?.v0;
        if (!api?.calculateDiscountedPrices) throw new Error('Regios is unavailable');
        let timeout;
        try {
          const result = await Promise.race([
            api.calculateDiscountedPrices({
              ...product,
              variants: [variant],
              variantId: variant.id,
              regularPriceInCents: variant.priceInCents,
              compareAtPriceInCents: variant.compareAtPriceInCents,
              quantity: 1
            }),
            new Promise((_, fail) => { timeout = setTimeout(() => fail(new Error('Regios timed out')), 12000); })
          ]);
          const sale = result?.salePrice?.amount;
          const regular = result?.regularPrice?.amount;
          if (typeof sale !== 'number' || typeof regular !== 'number' || !Number.isFinite(sale) || !Number.isFinite(regular) || sale < 0 || regular < 0) {
            throw new Error('Invalid Regios response');
          }
          const native = nativePrice(variant);
          return { sale: Math.min(native.sale, Math.round(sale * 100)), regular: Math.max(native.regular, Math.round(regular * 100)) };
        } finally {
          clearTimeout(timeout);
        }
      }});
      drain();
    });
    cache.set(key, pending);
    pending.catch(() => cache.delete(key));
    return pending;
  }

  function money(cents, compact = false) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: window.Shopify?.currency?.active || 'USD', currencyDisplay: 'narrowSymbol',
      ...(compact && cents >= 10000 ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {})
    }).format(cents / 100);
  }

  class CampaignPrice extends HTMLElement {
    connectedCallback() {
      try { this.product = JSON.parse(this.querySelector('[data-campaign-price-data]').textContent); }
      catch (_) { return; }
      // Share the card's wrapping label row with the countdown and other tags.
      this.badge = this.querySelector('[data-campaign-badge]') || this.badge;
      const card = this.closest('[data-campaign-product-card]');
      const labelContainer = card?.querySelector(':scope > .product-label-container');
      if (labelContainer && this.badge) labelContainer.appendChild(this.badge);
      this.refresh();
    }

    disconnectedCallback() { this.runId = (this.runId || 0) + 1; }

    async refresh() {
      if (!this.product?.variants?.length) return;
      const runId = this.runId = (this.runId || 0) + 1;
      this.render(summarize(this.product.variants.map(nativePrice)));
      if (this.dataset.source !== 'regios' || !window.RegiosDOPP?.api?.v0?.calculateDiscountedPrices) return;
      this.setAttribute('aria-busy', 'true');
      try {
        const prices = await Promise.all(this.product.variants.map(variant => calculate(this.product, variant)));
        if (this.isConnected && this.runId === runId) this.render(summarize(prices));
      } catch (_) {
        // Keep the complete native summary on failure; never advertise a
        // minimum/maximum derived from only a partial set of variants.
      } finally {
        if (this.runId === runId) this.removeAttribute('aria-busy');
      }
    }

    render(summary) {
      if (!summary) return;
      const sale = this.querySelector('[data-campaign-sale]');
      const regular = this.querySelector('[data-campaign-regular]');
      const badge = this.badge || this.querySelector('[data-campaign-badge]');
      if (sale) sale.textContent = money(summary.sale);
      if (regular) {
        regular.hidden = summary.regular <= summary.sale;
        regular.textContent = regular.hidden ? '' : money(summary.regular, true);
      }
      if (badge) {
        badge.hidden = this.dataset.showBadge !== 'true' || summary.percent <= 0;
        const template = summary.uniformDiscount ? this.dataset.uniformBadgeTemplate : this.dataset.badgeTemplate;
        badge.textContent = (template || '[percent]%').replace(/\[percent\]/g, String(summary.percent));
      }
      const card = this.closest('[data-discount-variant-card]');
      if (card) {
        card.dataset.discountPercent = String(summary.percent);
        this.dispatchEvent(new CustomEvent('campaign:price-updated', { bubbles: true }));
      }
    }
  }

  let refreshTimer;
  function refreshAll() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      revision += 1;
      cache.clear();
      document.querySelectorAll('campaign-price').forEach(price => price.refresh());
    }, 120);
  }
  window.addEventListener('regios-dopp:api-initialized', refreshAll);
  events.forEach(event => document.addEventListener(event, refreshAll));
  customElements.define('campaign-price', CampaignPrice);
})();
